import React, { useState } from 'react';
import { Box, Button, Typography, Paper, CircularProgress } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { selectGameObject } from '../gamestate/gameStateSlice';
import { advanceState } from '../gamestate/GameState';
import { selectAllPlayers } from '../player/playerSlice';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import Background from 'features/components/BackgroundEffect';
import useSound from "use-sound";
import { BrandLockup } from "components";
import testSound from 'sounds/test_sound.mp3';
import Provider from 'provider';
import logging from 'utils/logging';

const log = logging.getLogger('PregameScreen');

const ACCENT_TEAL = '#2ec4b6';
const ACCENT_YELLOW = '#ffe066';
const TEXT_COLOR = '#222';
const CARD_BG = '#fff';

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;


const Content = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4, 6),
  margin: 'auto',
  backgroundColor: CARD_BG,
  color: TEXT_COLOR,
  textAlign: 'center',
  maxWidth: 900,
  width: '100%',
  borderRadius: 20,
  boxShadow: '0 4px 24px rgba(44,62,80,0.08)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  animation: `${fadeInUp} 0.7s cubic-bezier(0.4,0,0.2,1)`,
}));

const logoAnimSx = {
  marginBottom: '24px',
  marginTop: '8px',
  animation: `${fadeInUp} 1s cubic-bezier(0.4,0,0.2,1)`,
};

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '2em',
  fontWeight: 700,
  marginBottom: theme.spacing(3),
  color: TEXT_COLOR,
  letterSpacing: 0.5,
  animation: `${fadeInUp} 0.9s cubic-bezier(0.4,0,0.2,1)`,
}));

const Instructions = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  textAlign: 'left',
  width: '100%',
}));

const InstructionItem = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  opacity: 0,
  animation: `${fadeInUp} 0.7s cubic-bezier(0.4,0,0.2,1) forwards`,
  animationDelay: `${theme.spacing(0.5)}`,
}));

const Number = styled(Box)(({ theme }) => ({
  background: ACCENT_TEAL,
  color: '#fff',
  width: 28,
  height: 28,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: '1.1em',
  flexShrink: 0,
  boxShadow: '0 2px 6px rgba(44,62,80,0.10)',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: ACCENT_TEAL,
  color: '#fff',
  padding: theme.spacing(1, 2),
  marginTop: theme.spacing(1),
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: '#25a89c',
  },
  boxShadow: '0 2px 4px rgba(44,62,80,0.10)',
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(120deg, ${ACCENT_YELLOW} 0%, ${ACCENT_TEAL} 50%, ${ACCENT_YELLOW} 100%)`,
  color: '#222',
  padding: theme.spacing(2, 4),
  fontSize: '1.2em',
  marginTop: theme.spacing(4),
  borderRadius: 12,
  fontWeight: 700,
  textTransform: 'none',
  boxShadow: '0 4px 16px rgba(44,62,80,0.40) !important',
  backgroundSize: '200% 200%',
  backgroundPosition: '0% 40%',
  transition: 'background-position 0.5s cubic-bezier(0.4,0,0.2,1), box-shadow 0s',
  '&:hover': {
    backgroundPosition: '100% 60%',
    boxShadow: '0 4px 16px rgba(44,62,80,0.40) !important',
  },
  '&:focus': {
    boxShadow: '0 4px 16px rgba(44,62,80,0.40) !important',
  },
  '&:active': {
    boxShadow: '0 4px 16px rgba(44,62,80,0.40) !important',
  },
}));

const MobileWarning = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: '#fff',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: theme.spacing(4),
}));

const PageWrapper = styled('div')({
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
});

const PregameScreen = () => {
  const dispatch = useDispatch();
  const game = useSelector(selectGameObject);
  const players = useSelector(selectAllPlayers);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  const [playTestSound] = useSound(testSound);

  // Mobile detection
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 700;

  const handleSoundTest = () => {
    playTestSound();
  };

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  /**
   * Comprehensive game initialization function
   * Resets host state and triggers audio generation for all narrations
   */
  const initializeGame = async () => {
    if (!game?.id) {
      log.error('No game ID available for initialization');
      return;
    }

    setIsInitializing(true);
    log.info('Starting comprehensive game initialization');

    try {
      // Trigger generation of all question narrations in the background
      log.info('Triggering generation of all question narrations for game', game.id);
      Provider.generateAllQuestionNarrations(game.id).catch(error => {
        log.error('Error generating all question narrations:', error);
      });

      // Trigger generation of all category name audio in the background
      log.info('Triggering generation of all category audio for game', game.id);
      Provider.generateAllCategoryAudio(game.id).catch(error => {
        log.error('Error generating all category audio:', error);
      });

      log.info('Successfully triggered pre-generation (background)');
    } catch (error) {
      log.error('Error during game initialization:', error);
    } finally {
      setIsInitializing(false);
    }
  };

  const handleStartGame = async () => {
    try {
      // Initialize the game (triggers background audio pre-generation)
      await initializeGame();

      // Then advance the game state
      await advanceState(game, players, dispatch);
    } catch (error) {
      log.error('Error starting game:', error);
      // Even if initialization fails, we can still start the game
      // The narrations will be generated on-demand as needed
      await advanceState(game, players, dispatch);
    }
  };

  // Determine button text based on initialization state
  const getButtonText = () => {
    if (isInitializing) return "Initializing Game...";
    return "Let's Play!";
  };

  if (isMobile) {
    return (
      <MobileWarning>
        <BrandLockup
          size="md"
          variant="light"
          orientation="vertical"
          sx={{ marginBottom: '24px' }}
        />
        <Typography variant="h5" sx={{ color: TEXT_COLOR, fontWeight: 700, mb: 2 }}>
          Please use a desktop or laptop
        </Typography>
        <Typography sx={{ color: TEXT_COLOR, maxWidth: 340 }}>
          This screen is designed for a larger display so everyone can see and hear the game clearly. For the best experience, please host the game from a desktop or laptop computer.
        </Typography>
      </MobileWarning>
    );
  }

  return (
    <>
      <Background />
      <PageWrapper>
        <Content>
          <BrandLockup
            size="lg"
            variant="light"
            orientation="horizontal"
            sx={logoAnimSx}
          />
          <Title variant="h1">Game Setup</Title>
          <Instructions>
            <InstructionItem style={{ animationDelay: '0.2s' }}>
              <Number>1</Number>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: TEXT_COLOR, fontWeight: 600 }}>
                  Choose How to Share the Game
                </Typography>
                <Typography sx={{ color: TEXT_COLOR }}>
                  Whether you're playing <strong>virtually</strong> or <strong>in person</strong>, make sure everyone can see this screen:
                  <ul>
                    <li>On Zoom, Google Meet, or Teams? Just share your screen.</li>
                    <li>In the same room? Cast to a TV, plug in with HDMI, or gather around one screen—whatever works best.</li>
                  </ul>
                </Typography>
              </Box>
            </InstructionItem>
            <InstructionItem style={{ animationDelay: '0.4s' }}>
              <Number>2</Number>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: TEXT_COLOR, fontWeight: 600 }}>
                  Make the Game Full Screen
                </Typography>
                <Typography sx={{ color: TEXT_COLOR }}>
                  Click the button below to make the game take up your entire screen
                </Typography>
                <ActionButton
                  variant="contained"
                  onClick={handleFullscreen}
                  startIcon={<FullscreenIcon />}
                >
                  {isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                </ActionButton>
              </Box>
            </InstructionItem>
            <InstructionItem style={{ animationDelay: '0.6s' }}>
              <Number>3</Number>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: TEXT_COLOR, fontWeight: 600 }}>
                  Confirm Visibility
                </Typography>
                <Typography sx={{ color: TEXT_COLOR }}>
                  Double-check that <strong>everyone can see</strong> this screen.
                </Typography>
              </Box>
            </InstructionItem>
            <InstructionItem style={{ animationDelay: '0.8s' }}>
              <Number>4</Number>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: TEXT_COLOR, fontWeight: 600 }}>
                  Test Sound
                </Typography>
                <Typography sx={{ color: TEXT_COLOR }}>
                  Make sure your device's volume is up and everyone can hear the game sounds.
                </Typography>
                <ActionButton
                  variant="contained"
                  onClick={handleSoundTest}
                >
                  Test Sound
                </ActionButton>
              </Box>
            </InstructionItem>
          </Instructions>
          <GradientButton
            variant="contained"
            onClick={handleStartGame}
            size="large"
            disabled={isInitializing}
          >
            {getButtonText()}
            {isInitializing && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </GradientButton>
        </Content>
      </PageWrapper>
    </>
  );
};

export default PregameScreen; 